import { useContext, useState } from "react";
import { styled } from "@mui/material";

import { DataContext } from "contexts/Data";
import { StreamContext } from "contexts/Stream";
import useVariant from "hooks/useVariant";
import { DEMO } from "./utils/createGL";
import Visualization2D from "./Visualization2D";
import CenterPopup from "components/CenterPopup";
import { PauseCircleOutline } from "@mui/icons-material";
import InteractionGuide from "components/interactionGuide/InteractionGuide";
import {
  ANIMATIONS,
  STREAM_TYPES,
  turntable_be6_sequence,
  turntable_xev_9e_sequence,
} from "shared/enum";
import useSelectedColor from "hooks/useSelectedColor";

const LOCALSTORAGEKEY = "popup";
const VALUE = "guide";

function Turntable({
  turntableIsEnabled,
  isMobile,
  drawerOpen,
  closeDrawer,
  handleLoadScreen,
}: {
  turntableIsEnabled: boolean;
  isMobile: boolean;
  drawerOpen: boolean;
  closeDrawer: VoidFunction;
  handleLoadScreen: (val: boolean) => void;
}) {
  const { cachedState, configuration } = useContext(DataContext);
  const isGuided = localStorage.getItem(LOCALSTORAGEKEY) === VALUE;
  const [interactGuide, setInteractionGuide] = useState(!isGuided);
  const { id: colorId, friendly_name } = useSelectedColor();

  const {
    scenarios,
    demoState: { sunState, animationCommands },
    turntableState,
    streamViewType,
  } = useContext(StreamContext);

  const variant = useVariant();
  const { isTurntable, turntableCameraNamePrefix } = turntableState;
  const isIngloPlatform = streamViewType === STREAM_TYPES.IN_GLO;

  // const turnCameras = cameras?.filter((_cam) => _cam.name?.includes("turn"));
  // const defaultVariants = variant
  //   ? [
  //       configuration.modelDefaults[variant.code].colorId,
  //       configuration.modelDefaults[variant.code].friendly_name,
  //     ]
  //   : [];

  const defaultVariants = variant ? [colorId, friendly_name] : [];

  const [state, setState] = useState({
    transition: "directionalwarp",
    reverse: false,
    selectedTurnFrame: DEMO.INITIAL.TURN_FRAME,
  });

  const handleFrameChange = (frame: number): void => {
    setState((s) => ({
      ...s,
      transition: "wipe",
      reverse:
        (frame < s.selectedTurnFrame && s.selectedTurnFrame !== DEMO.TURNTABLE.TOTAL_FRAMES - 1) ||
        (s.selectedTurnFrame === 0 && frame === DEMO.TURNTABLE.TOTAL_FRAMES - 1),
      selectedTurnFrame: frame,
    }));
  };

  // const newMode = cachedState.animations.dayMode;
  // const ICON_COMMAND = newMode ? "Icon_OFF" : "Icon_ON_NIGHT";
  // const LAMP_COMMAND = newMode ? "Lamps_OFF" : "Lamps_ON";
  // const DRLS_COMMAND = newMode ? "DRL_OFF" : "DRL_ON";

  // const filteredKit = selectedAccessories.kit.filter((item) => item.startsWith("ACC_"));
  // const filteredExterior = selectedAccessories.exterior.filter((item) => item.startsWith("ACC_"));
  // const filteredInterior = selectedAccessories.interior.filter((item) => item.startsWith("ACC_"));

  // const allCombinedAccessories = [...filteredKit, ...filteredExterior, ...filteredInterior];

  // let selectedVariantsArray = [
  //   cachedState.variant?.friendly_name,
  //   cachedState.color?.friendly_name,
  //   ICON_COMMAND,
  //   LAMP_COMMAND,
  //   DRLS_COMMAND,
  //   leftSideVariants,
  //   // ...allCombinedAccessories,
  //   // ...turntableAccessoriesVariants,
  // ];

  // if (leftSideVariants && leftSideVariants !== "") {
  //   selectedVariantsArray = selectedVariantsArray.filter(
  //     (variant) =>
  //       !variant.includes("Lamps") && !variant.includes("DRL") && !variant.includes("Icon"),
  //   );

  //   selectedVariantsArray = [...selectedVariantsArray, leftSideVariants];
  // }

  // const selectedVariants = [...new Set(selectedVariantsArray)];

  // ANIMATIONS

  const currentVariants = isIngloPlatform
    ? []
    : cachedState.animations.headlights
      ? [...variant.lamps_on_variant_ext, ...variant.lamps_on_variant_int]
      : [...variant.lamps_off_variant_ext, ...variant.lamps_off_variant_int];
  const newCurrentVariant = [
    ...new Set([...defaultVariants, variant.friendly_name, ...currentVariants]),
  ];
  const ism110 = variant.code === "BE 6";

  const arrangeAnimationCommand = () => {
    const sequence = ism110 ? turntable_be6_sequence : turntable_xev_9e_sequence;
    return animationCommands.sort((a, b) => {
      const keyA = a.split("|")[0];
      const keyB = b.split("|")[0];
      return sequence.indexOf(keyA) - sequence.indexOf(keyB);
    });
  };

  return (
    <div style={{ display: "grid", height: "100%", width: "100%", overflow: "hidden" }}>
      <InteractionGuideStyled
        isActive={interactGuide}
        buttonText="Thanks. I got it."
        switchLabel="Don't show again"
        handleClose={() => {
          setInteractionGuide(false);
        }}
        handleSwichtChange={(e) => {
          if (e.target.checked) {
            localStorage.setItem(LOCALSTORAGEKEY, VALUE);
            setInteractionGuide(false);
          }
        }}
      />
      {!turntableIsEnabled && isTurntable && !isMobile && (
        // <Hint>Turntable is paused while configuring</Hint>
        <CenterPopup
          message="Turntable is paused while configuring"
          btnText="Collapse Configurator"
          btnAction={() => {
            closeDrawer();
          }}
          headerIcon={<PauseCircleOutline sx={{ color: "inherit", width: 25, height: 25 }} />}
        />
      )}
      <Visualization2D
        drawerOpen={drawerOpen}
        isMobile={isMobile}
        inTransition
        reverse={state.reverse}
        selectedEnv={0}
        // src={`${process.env.REACT_APP_APEX_BASE_URL}/get-image/${cachedState.model?.id}?&width=1920&transparent=false&camera=${turntableCameraNamePrefix}&options=${selectedVariants?.filter(Boolean).toString()}&env=${scenarios[0]?.name}&daytime=${sunState.dayTime}&northyaw=${sunState.northyaw}&enable_dev=false&animations=${animationCommands}`}
        src={`${process.env.REACT_APP_APEX_BASE_URL_V3}/get-image/${cachedState.model?.id}?&width=1920&transparent=false&camera=${turntableCameraNamePrefix}&options=${newCurrentVariant.toString()}&env=${isIngloPlatform ? scenarios[1]?.name : scenarios[0]?.name}&daytime=${sunState.dayTime}&northyaw=${sunState.northyaw}&enable_dev=false&animations=${arrangeAnimationCommand()?.filter((item) => item !== "")}`}
        // src="https://production.live.rod.solution-server.com/api/v1/get-image/7vxw5lqgorg0men?env=STUDIO_CALVERT&daytime=18.2&northyaw=195&camera=turn_11&options=&transparent=false&enable_dev=true&width=1920 "
        transition={state.transition}
        turnFrame={state.selectedTurnFrame}
        handleFrameChange={(frame) => {
          turntableIsEnabled && handleFrameChange(frame);
        }}
        onLoad={() => console.log("on load")}
        onMouseDown={() => console.log("on mouse down")}
        turntableIsEnabled={turntableIsEnabled}
        dayMode={cachedState.animations.dayMode}
        handleNotLoaded={handleLoadScreen}
      />
    </div>
  );
}

export default Turntable;

const Hint = styled("p")(({ theme }) => ({
  gridColumnStart: 1,
  gridColumnEnd: -2,
  gridRowStart: 1,
  gridRowEnd: -1,
  paceSelf: "end center",
  zIndex: 100,
  justifySelf: "center",
  alignSelf: "end",
  background: "rgba(0,0,0,0.5)",
  color: "white",
  borderRadius: theme.shape.borderRadius,
  backdropFilter: "blur(1px)",
  padding: "7px 15px",
  marginBottom: "120px",
  fontSize: "0.8em",
  [theme.breakpoints.down("md")]: {
    gridColumnEnd: -1,
    gridRowEnd: -2,
    marginBottom: "80px",
  },
}));

const InteractionGuideStyled = styled(InteractionGuide)(({ theme }) => ({
  gridArea: "center",
  justifySelf: "center",
  alignSelf: "center",
  position: "absolute",
  zIndex: 10,
  left: "calc(50% - 125px)",
  top: "calc(50% - 125px)",

  [theme.breakpoints.down("sm")]: {
    left: "20%",
    top: "30%",
  },
}));
